import React, { useCallback, useEffect, useRef, useState } from "react";

/** @jsx jsx */
import { jsx, css, keyframes } from '@emotion/react';
import { Modal } from "./Modal";
import { UIButtonSmall } from "../styles/ButtonStyle";
import { GameClient } from "../gameClient";
import { TooltipInfo, TutorialId, tooltipInfo } from "./TooltipInfo";
import { PlayerConstants, SimulationConstants } from "../../../shared/constants";
import { ElementDisplayInfo } from "../../../shared/elementDisplayInfo";
import { lineArtLocations } from "../../../shared/generated/assetLocations";
import { TrainingLevel } from "../../../shared/playerAccount";
import { getTrainingLevel } from "../userSettings";
import { vec2 } from "@tlaukkan/tsm";
import { getDifferenceFromLastTrainingLevel } from "../../../shared/immuneCellTiers";
import { GameElement } from "../../../shared/gameElementTypes";

declare var gameClient: GameClient;
interface TrainingLevelModalProps {
  fullscreen: boolean;
  onClose?: () => void;
}

const containerStyle = css`
    display:flex;
    flex-direction:column;
    align-items:center;
    justify-content:center;
`;

const controlsStyle = css`
    display:flex;
    justify-content:space-between;
    align-items:center;
`;

const textStyle = css`
    font-size: 25px;
    margin-bottom: 1vh;
    margin-top: 1vh;
    min-height: 4vh;
    padding-left: 3.5vw;
    padding-right: 3.5vw;
`;

/** One newly unlocked cell, a component of its own so its hooks run once per cell rather than inside a map */
const UnlockedCell = ({ gameElement }: { gameElement: GameElement }) => {
  const element = ElementDisplayInfo.get(gameElement)!;
  const assetLocation = (lineArtLocations as any)[element.asset];
  const hover = useCallback(() => {
    gameClient.setInspection({
      element: gameElement,
      position: vec2.zero.copy(),
      hideTooltips: true
    });
  }, [gameElement]);

  const mouseLeave = useCallback(() => {
    gameClient.setInspection(null);
  }, []);
  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
    }}
      onMouseOver={hover}
      onMouseOut={mouseLeave}
    >
      <span style={{ marginBottom: "7px" }}>{element?.displayName}</span>
      <svg x="142" y="75" width="64" height="64" clipPath="url(#clip)">
        <defs>
          <clipPath id="clip">
            <rect x="0" y="0" width="64" height="64"></rect>
          </clipPath>
        </defs>

        <image x={assetLocation.x} y={-assetLocation.y} href="assets/lineArtSpriteSheet.png" style={{
          transform: "scale(0.5)"
        }} />
      </svg>
    </div>
  );
};

const UnlockedCellList = ({ trainingLevel }: { trainingLevel: TrainingLevel }) => (
  <div style={{
    display: "flex",
    flexWrap: "wrap", //Reaching Trained unlocks more than fit on one row
    justifyContent: "center",
    gap: "18px",
    width: "90%",
    margin: "auto",
  }}>
    {getDifferenceFromLastTrainingLevel(trainingLevel).map(gameElement =>
      <UnlockedCell key={gameElement} gameElement={gameElement} />
    )}
  </div>
);

export const TrainingLevelModal = (props: TrainingLevelModalProps) => {
  const trainingLevel = getTrainingLevel();
  return (
    <Modal isOpen={true} onClose={props.onClose} fullScreen={props.fullscreen}>
      <div css={containerStyle}>
        <h1>{trainingLevel === TrainingLevel.Trained ?
          <span>Test your skills against other players!</span>
          :
          <span>The Viruses Have Gotten Stronger!</span>
        }</h1>
        <div style={{
          objectFit: "contain",
          width: "640px",
          minHeight: "360px",
        }}>
          {
            trainingLevel === TrainingLevel.Trained ?
              <>
                <p>All immune cells have been unlocked!</p>
                <p>Multiplayer is now available, you can now play against other players!</p>
                <p>Good luck!</p>
              </>
              :
              <ul>
                <li>You now start with {PlayerConstants.StartingHpByTrainingLevel[trainingLevel]} blood cells!</li>
                <li>There are now {SimulationConstants.MaxRoundsByTrainingLevel[trainingLevel]} rounds!</li>
                <li>New Immune Cells are available!</li>
              </ul>
          }
          <UnlockedCellList trainingLevel={trainingLevel} />
        </div>
      </div>
    </Modal>
  );
};
